
import { Component, Vue, Watch } from 'vue-property-decorator'
import html2canvas from 'html2canvas'
import { uploadBase64Image } from '@/utils/util'
import { editStoreInfo } from '@/api/store'
import { Getter } from 'vuex-class'

@Component
export default class AutoCreateIcon extends Vue {
    @Getter companyId!: string
    @Getter info!: any
    color = '#2979FF'
    storeName = ''
    focusTimer: any = null
    colorList = [
        {
            id: 1,
            color: '#2979FF'
        },
        {
            id: 2,
            color: '#1976D2'
        },
        {
            id: 3,
            color: '#1565C0'
        },
        {
            id: 4,
            color: '#0D47A1'
        },
        {
            id: 5,
            color: '#3949AB'
        },
        {
            id: 6,
            color: '#F59E0B'
        },
        {
            id: 7,
            color: '#43AA8B'
        },
        {
            id: 8,
            color: '#38B000'
        },
        {
            id: 9,
            color: '#E64120'
        },
        {
            id: 10,
            color: '#795548'
        },
    ]
    beforeDestroy() {
        // 清理定时器
        if (this.focusTimer) {
            clearTimeout(this.focusTimer)
            this.focusTimer = null
        }
    }

    selectColor(color: string) {
        this.color = color
    }

    // 处理输入框获得焦点
    handleInputFocus() {
        // 清除之前的定时器
        if (this.focusTimer) {
            clearTimeout(this.focusTimer)
            this.focusTimer = null
        }

        // 延迟滚动到顶部，让键盘先弹出
        this.focusTimer = setTimeout(() => {
            window.scrollTo({
                top: 170,
                behavior: 'smooth'
            })
        }, 300) // 300ms后滚动到顶部
    }

    @Watch('storeName')
    onStoreNameChange(val: string) {
        if (!val) return
        const maxLen = 10
        let len = 0
        let newStr = ''
        for (let i = 0; i < val.length; i++) {
            const char = val.charAt(i)
            const charLen = /[\u4e00-\u9fa5]/.test(char) ? 2 : 1
            if (len + charLen > maxLen) {
                break
            }
            len += charLen
            newStr += char
        }
        if (newStr !== val) {
            this.storeName = newStr
        }
    }

    // 根据字符数量返回样式类名
    get textSizeClass() {
        const len = this.getWeightedLength(this.storeName)
        // 如果包含非汉字字符，强制使用size-6
        if (!this.isAllChinese) return 'size-6'
        
        if (len <= 2) return 'size-1'  // 相当于1个汉字
        if (len <= 4) return 'size-2'  // 相当于2个汉字
        if (len <= 6) return 'size-3'  // 相当于3个汉字
        if (len <= 8) return 'size-4'  // 相当于4个汉字
        if (len <= 10) return 'size-5' // 相当于5个汉字
        return ''
    }

    // 判断是否全部为汉字（只要包含非汉字就算false）
    get isAllChinese() {
        if (!this.storeName) return true
        // 检查每个字符是否都是汉字
        return /^[\u4e00-\u9fa5]+$/.test(this.storeName)
    }

    // 计算加权长度（汉字算2字符）
    getWeightedLength(val: string) {
      if (!val) return 0
      return Array.from(val).reduce((count, char) => {
        return count + (/[\u4e00-\u9fa5]/.test(char) ? 2 : 1)
      }, 0)
    }

    // 4-5个字时，拆分为两行（对应加权长度 7-10）
    get storeNameLine1() {
        const len = this.getWeightedLength(this.storeName)
        if (len >= 7 && len <= 10) {
            // 这里简单处理，取前两个字（假设是汉字）
            // 如果混合输入，可能需要更复杂的截取逻辑，暂维持原逻辑截取前2个字符
            // 或者：如果是纯汉字，取前2个；如果是混合，可能需要按长度截取
            // 简单起见，仍然按字符数截取一半，或者按原逻辑
            // 原逻辑：return this.storeName.substring(0, 2)
            // 建议：如果全是汉字，取前2个；否则... 这里保持原逻辑简单截取，后续可视效果调整
            return this.storeName.substring(0, 2)
        }
        return ''
    }

    get storeNameLine2() {
        const len = this.getWeightedLength(this.storeName)
        if (len >= 7 && len <= 10) {
            return this.storeName.substring(2)
        }
        return ''
    }

    // 将图标转换为 base64
    async convertToBase64() {
        const element = this.$refs.iconPreview as HTMLElement
        if (!element) {
            this.$toast.fail('图标元素未找到')
            return null
        }

        try {
            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff', // 透明背景
                scale: 2, // 提高清晰度
                useCORS: true, // 允许跨域图片
                logging: false,
                allowTaint: false,
                removeContainer: true,
            })
            
            // 确保使用 PNG 格式以支持透明背景
            const base64Image = canvas.toDataURL('image/png', 1.0)
            return base64Image
        } catch (error) {
            console.error('转换图片失败:', error)
            this.$toast.fail('生成图片失败')
            return null
        }
    }

    async onConfirm() {
        if (!this.storeName) {
            this.$toast('请输入店铺简称')
            return
        }

        try {
            // 显示生成提示
            this.$toast({
                message: '生成中...',
                forbidClick: true,
                duration: 0
            })

            // 转换图标为 base64
            const base64Image = await this.convertToBase64()
            
            if (!base64Image) {
                this.$toast.clear()
                return
            }


            // 使用公共方法上传图片（使用时间戳命名，强制使用 PNG 格式以保持透明背景）
            const result = await uploadBase64Image(base64Image, undefined, '1')
            this.$toast.clear()
            
            if (result.success && result.filePath) {
                // 如果是从店铺信息页面进入，调用更新接口
                if (this.$route.query.fromSource === 'storeInfo') {
                    try {
                        const updateRes = await editStoreInfo({
                            storeId: this.companyId,
                            storeHeadPortrait: result.filePath
                        })
                        if (updateRes.code === '000000') {
                            this.$toast.success('修改成功')
                            // 更新store信息
                            const logoUrl = window.location.origin + this.$downloadUrl(result.filePath) // 完整的url地址
                            this.$store.dispatch('GetInfo', { ...this.info, logoUrl })
                            
                            this.$router.back()
                        } else {
                            this.$toast.fail(updateRes.msg || '修改失败')
                        }
                    } catch (e) {
                        console.error(e)
                        this.$toast.fail('修改失败')
                    }
                    return
                }

                // 上传成功，返回到创建店铺页面并传递文件路径
                // 更新本地缓存，确保返回时能读取到最新图片
                try {
                  const cachedFormStr = sessionStorage.getItem('createStoreFormCache')
                  let cachedForm = {}
                  if (cachedFormStr) {
                    cachedForm = JSON.parse(cachedFormStr)
                  }
                  // @ts-ignore
                  cachedForm.storeHeadPortrait = result.filePath
                  sessionStorage.setItem('createStoreFormCache', JSON.stringify(cachedForm))
                } catch (e) {
                  console.error('更新缓存失败', e)
                }
                this.$router.back()
            } else {
                this.$toast.fail(result.message || '上传失败')
            }
        } catch (error) {
            this.$toast.clear()
            this.$toast.fail('操作失败，请重试')
            console.error('生成或上传图标失败:', error)
        }
    }
}
